'use client';

/**
 * FeatureHint Component
 *
 * A contextual help icon that displays informative tooltips.
 * Use next to section headers, form labels, or complex UI elements
 * to provide inline guidance without cluttering the interface.
 */

import HelpIcon from '@mui/icons-material/HelpOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import TipIcon from '@mui/icons-material/LightbulbOutlined';
import { alpha, Box, Fade, Link, Popper, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, { useCallback, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { glassEffects } from '../../theme/shadows';

// ============================================================================
// Types
// ============================================================================

export type FeatureHintVariant = 'info' | 'help' | 'tip';
export type FeatureHintSize = 'small' | 'medium' | 'large';
export type FeatureHintPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface FeatureHintProps {
  /** The hint content (text or React node) */
  content: React.ReactNode;
  /** Optional title for the hint */
  title?: string;
  /** Visual variant */
  variant?: FeatureHintVariant;
  /** Icon size */
  size?: FeatureHintSize;
  /** Tooltip placement */
  placement?: FeatureHintPlacement;
  /** Optional "Learn more" link */
  learnMoreUrl?: string;
  /** Custom learn more text */
  learnMoreText?: string;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Whether to show inline with text (adds margin) */
  inline?: boolean;
  /** Disable the hint */
  disabled?: boolean;
  /** Delay before showing tooltip (ms) */
  enterDelay?: number;
  /** Delay before hiding tooltip (ms) */
  leaveDelay?: number;
  /** Maximum width of tooltip */
  maxWidth?: number;
  /** Custom class name */
  className?: string;
  /** Keyboard shortcut hint */
  shortcut?: string[];
}

// ============================================================================
// Styled Components
// ============================================================================

interface IconButtonProps {
  $variant: FeatureHintVariant;
  $size: FeatureHintSize;
  $inline: boolean;
  $disabled: boolean;
}

const sizeMap: Record<FeatureHintSize, number> = {
  small: 14,
  medium: 18,
  large: 22,
};

const IconWrapper = styled('span', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<IconButtonProps>(({ theme, $variant, $size, $inline, $disabled }) => {
  const getColor = () => {
    if ($disabled) return theme.palette.text.disabled;
    switch ($variant) {
      case 'tip':
        return theme.palette.warning.main;
      case 'help':
        return theme.palette.info.main;
      case 'info':
      default:
        return theme.palette.text.secondary;
    }
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: $disabled ? 'default' : 'help',
    color: getColor(),
    marginLeft: $inline ? theme.spacing(0.75) : 0,
    opacity: $disabled ? 0.5 : 0.7,
    transition: 'all 150ms ease',
    borderRadius: '50%',
    padding: 2,

    '&:hover': {
      opacity: $disabled ? 0.5 : 1,
      backgroundColor: $disabled ? 'transparent' : alpha(getColor(), 0.1),
    },

    '& svg': {
      width: sizeMap[$size],
      height: sizeMap[$size],
    },
  };
});

interface TooltipContainerProps {
  $isDark: boolean;
  $maxWidth: number;
}

const TooltipContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TooltipContainerProps>(({ theme, $isDark, $maxWidth }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    maxWidth: $maxWidth,
    background: $isDark ? 'rgba(13, 33, 55, 0.98)' : 'rgba(255, 255, 255, 0.98)',
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: `0 8px 24px -4px rgba(0, 0, 0, 0.2), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}`,
  };
});

const ShortcutContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const KeyboardKey = styled('kbd')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 5px',
  fontSize: '0.6875rem',
  fontFamily: 'inherit',
  fontWeight: 600,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  boxShadow: `0 1px 0 ${theme.palette.divider}`,
}));

// ============================================================================
// Component
// ============================================================================

export const FeatureHint: React.FC<FeatureHintProps> = ({
  content,
  title,
  variant = 'info',
  size = 'medium',
  placement = 'top',
  learnMoreUrl,
  learnMoreText,
  icon,
  inline = true,
  disabled = false,
  enterDelay = 200,
  leaveDelay = 150,
  maxWidth = 280,
  className,
  shortcut,
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Use i18n for default values
  const displayLearnMoreText = learnMoreText ?? t('hint.learnMore');

  const getIcon = () => {
    if (icon) return icon;
    switch (variant) {
      case 'tip':
        return <TipIcon />;
      case 'help':
        return <HelpIcon />;
      case 'info':
      default:
        return <InfoIcon />;
    }
  };

  const handleEnter = useCallback(() => {
    if (disabled) return;
    clearTimeout(leaveTimeoutRef.current);
    enterTimeoutRef.current = setTimeout(() => setOpen(true), enterDelay);
  }, [disabled, enterDelay]);

  const handleLeave = useCallback(() => {
    clearTimeout(enterTimeoutRef.current);
    leaveTimeoutRef.current = setTimeout(() => setOpen(false), leaveDelay);
  }, [leaveDelay]);

  const handleFocus = useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <IconWrapper
        ref={anchorRef}
        $variant={variant}
        $size={size}
        $inline={inline}
        $disabled={disabled}
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={title ?? t('hint.moreInfo')}
        aria-describedby={open ? 'feature-hint-tooltip' : undefined}
      >
        {getIcon()}
      </IconWrapper>
      <Popper
        id="feature-hint-tooltip"
        open={open}
        anchorEl={anchorRef.current}
        placement={placement}
        transition
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
          {
            name: 'preventOverflow',
            options: { padding: 16 },
          },
        ]}
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <TooltipContainer
              $isDark={isDark}
              $maxWidth={maxWidth}
              onMouseEnter={() => clearTimeout(leaveTimeoutRef.current)}
              onMouseLeave={handleLeave}
            >
              {title && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  {title}
                </Typography>
              )}

              <Typography
                variant="body2"
                component="div"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {content}
              </Typography>

              {shortcut && shortcut.length > 0 && (
                <ShortcutContainer>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mr: 0.5,
                    }}
                  >
                    {t('hint.shortcut')}
                  </Typography>
                  {shortcut.map((key, i) => (
                    <React.Fragment key={i}>
                      <KeyboardKey>{key}</KeyboardKey>
                      {i < shortcut.length - 1 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          +
                        </Typography>
                      )}
                    </React.Fragment>
                  ))}
                </ShortcutContainer>
              )}

              {learnMoreUrl && (
                <Link
                  href={learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{
                    display: 'inline-block',
                    mt: 1,
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {displayLearnMoreText} →
                </Link>
              )}
            </TooltipContainer>
          </Fade>
        )}
      </Popper>
    </>
  );
};

FeatureHint.displayName = 'FeatureHint';

export default FeatureHint;
